import { Component, Input, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BaseChartDirective } from 'ng2-charts';
import { ChartConfiguration, ChartType, ChartData, ChartOptions } from 'chart.js';
import { EntityChartDataDto, ChartDataPointDto } from '../../../../models/chart.models';

/**
 * Reusable chart wrapper component
 * Supports line, bar, area, pie, and doughnut charts
 */
@Component({
  selector: 'app-chart-wrapper',
  standalone: true,
  imports: [CommonModule, BaseChartDirective],
  template: `
    <div class="chart-container" [style.height.px]="height">
      <div *ngIf="loading" class="chart-loading">
        <div class="loading-spinner"></div>
        <span>Loading chart...</span>
      </div>
      
      <div *ngIf="error" class="chart-error">
        <i class="fas fa-exclamation-triangle text-amber-500 text-2xl mb-2"></i>
        <p>{{ error }}</p>
        <button (click)="onRetry()" class="retry-btn">
          <i class="fas fa-redo mr-1"></i> Retry
        </button>
      </div>
      
      <div *ngIf="!loading && !error && hasData" class="chart-content">
        <canvas baseChart
          [data]="chartData"
          [options]="chartOptions"
          [type]="chartType">
        </canvas>
      </div>
      
      <div *ngIf="!loading && !error && !hasData" class="chart-empty">
        <i class="fas fa-chart-bar text-gray-300 text-4xl mb-2"></i>
        <p>No data available for this chart</p>
      </div>
    </div>
  `,
  styles: [`
    .chart-container {
      position: relative;
      width: 100%;
      min-height: 200px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    
    .chart-content {
      width: 100%;
      height: 100%;
    }
    
    .chart-loading,
    .chart-error,
    .chart-empty {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      text-align: center;
      color: #6b7280;
    }
    
    .loading-spinner {
      width: 40px;
      height: 40px;
      border: 3px solid #e5e7eb;
      border-top: 3px solid #10b981;
      border-radius: 50%;
      animation: spin 1s linear infinite;
      margin-bottom: 0.5rem;
    }
    
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
    
    .retry-btn {
      margin-top: 0.5rem;
      padding: 0.5rem 1rem;
      background: #10b981;
      color: white;
      border: none;
      border-radius: 0.375rem;
      cursor: pointer;
      transition: background 0.2s;
    }
    
    .retry-btn:hover {
      background: #059669;
    }
  `]
})
export class ChartWrapperComponent implements OnChanges {
  @ViewChild(BaseChartDirective) chart?: BaseChartDirective;

  @Input() data: EntityChartDataDto | null = null;
  @Input() title: string = '';
  @Input() type: 'line' | 'bar' | 'pie' | 'doughnut' = 'line';
  @Input() colors: string[] = ['#10b981'];
  @Input() height: number = 300;
  @Input() loading: boolean = false;
  @Input() error: string = '';
  @Input() showLegend: boolean = true;
  @Input() fill: boolean = false;
  @Input() retryCallback?: () => void;

  chartType: ChartType = 'line';
  chartData: ChartData = { labels: [], datasets: [] };
  chartOptions: ChartOptions = {};

  get hasData(): boolean {
    const dataPoints = this.data?.dataPoints;
    if (!dataPoints || dataPoints.length === 0) return false;

    // Check if we can extract any meaningful data
    const { labels, values } = this.extractDataPoints(dataPoints);
    return labels.length > 0 && values.some(v => v > 0);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['type']) {
      this.chartType = this.type as ChartType;
    }

    if (changes['data'] || changes['colors'] || changes['fill'] || changes['showLegend']) {
      this.updateChart();
    }
  }

  /**
   * Extract labels and values from data points, handling different API formats
   */
  private extractDataPoints(dataPoints: ChartDataPointDto[]): { labels: string[], values: number[] } {
    const labels: string[] = [];
    const values: number[] = [];

    for (const dp of dataPoints) {
      // Try to get label from various possible property names
      const label = dp.label || dp.date || dp.period || dp.timestamp ||
        (dp as any).name || (dp as any).x || '';

      // Try to get value from various possible property names
      const value = dp.value ?? dp.count ?? dp.total ??
        (dp as any).y ?? (dp as any).amount ?? 0;

      if (label) {
        labels.push(this.formatLabel(label));
        values.push(typeof value === 'number' ? value : parseFloat(value) || 0);
      }
    }

    return { labels, values };
  }

  /**
   * Format label for display (handle date strings, etc.)
   */
  private formatLabel(label: string): string {
    // Check if it's an ISO date string
    if (label && label.includes('T') && label.includes('-')) {
      try {
        const date = new Date(label);
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString('en-IN', {
            month: 'short',
            day: 'numeric'
          });
        }
      } catch {
        // Not a valid date, return as-is
      }
    }
    return label;
  }

  private updateChart(): void {
    if (!this.data?.dataPoints?.length) {
      return;
    }

    const { labels, values } = this.extractDataPoints(this.data.dataPoints);

    if (labels.length === 0) {
      return;
    }

    this.chartData = {
      labels: labels,
      datasets: [{
        label: this.data.entityName || this.title,
        data: values,
        backgroundColor: this.type === 'pie' || this.type === 'doughnut'
          ? this.generateColors(values.length)
          : this.hexToRgba(this.colors[0] || '#10b981', 0.6),
        borderColor: this.colors[0] || '#10b981',
        borderWidth: this.type === 'line' ? 2 : 1,
        fill: this.fill,
        tension: 0.4,
        pointBackgroundColor: this.colors[0] || '#10b981',
        pointBorderColor: '#fff',
        pointHoverBackgroundColor: '#fff',
        pointHoverBorderColor: this.colors[0] || '#10b981',
        barThickness: 'flex' as any,
        maxBarThickness: 50,
      }]
    };

    this.chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      animation: {
        duration: 0 // Disable animation to prevent shaking
      },
      plugins: {
        legend: {
          display: this.showLegend,
          position: 'top',
        },
        title: {
          display: !!this.title,
          text: this.title,
          font: {
            size: 14,
            weight: 'bold'
          }
        },
        tooltip: {
          mode: 'index',
          intersect: false,
        }
      },
      scales: this.type === 'pie' || this.type === 'doughnut' ? {} : {
        x: {
          grid: {
            display: false
          },
          ticks: {
            maxTicksLimit: 15, // Limit number of x-axis labels
            maxRotation: 45,
            minRotation: 0,
            autoSkip: true
          }
        },
        y: {
          beginAtZero: true,
          grid: {
            color: '#f3f4f6'
          },
          ticks: {
            precision: 0
          }
        }
      },
      interaction: {
        mode: 'nearest',
        axis: 'x',
        intersect: false
      },
      elements: {
        point: {
          radius: labels.length > 30 ? 0 : 4, // Hide points for large datasets
          hoverRadius: 6
        },
        line: {
          tension: 0.3
        }
      }
    };

    // Force chart update with no animation
    setTimeout(() => {
      this.chart?.update('none');
    }, 0);
  }

  private hexToRgba(hex: string, alpha: number): string {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  private generateColors(count: number): string[] {
    const baseColors = [
      '#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6',
      '#06b6d4', '#ec4899', '#84cc16', '#f97316', '#6366f1'
    ];

    const colors: string[] = [];
    for (let i = 0; i < count; i++) {
      colors.push(baseColors[i % baseColors.length]);
    }
    return colors;
  }

  onRetry(): void {
    if (this.retryCallback) {
      this.retryCallback();
    }
  }
}

